<script setup lang="ts">
import type { InputPrimaryEvent, NativeSelectOption } from '@shared/ui/fields'
import type { ScreenLayout } from '../../../model/assessments/types'

import { AppText } from '@shared/ui/typography'
import { InputPrimary, NativeSelect } from '@shared/ui/fields'
import { BtnCheckbox } from '@shared/ui/buttons'

import { useScreenEditor } from '../../../model/assessments/screen-editor.hook'
import ContentBlockList from './ContentBlockList.vue'
import ActivityList from './ActivityList.vue'

defineOptions({
  name: 'ScreenEditor',
})

const {
  refScreen,
  refHasScreens,
  refOwner,
  refLayout,
  refLayoutOptions,
  refCta,
  refIsSubmitScreen,
  setLayout,
  setCta,
  toggleSubmitScreen,
} = useScreenEditor()
</script>

<template>
  <div class="screen-editor">
    <AppText
      v-if="!refHasScreens"
      name="body2"
      color="slate-500"
    >
      This assessment has no screens yet. Add one in the rail to start authoring.
    </AppText>

    <AppText
      v-else-if="!refScreen"
      name="body2"
      color="slate-500"
    >
      Select a screen in the rail to edit it.
    </AppText>

    <!-- Four sections, separated by a rule: Layout, Content, Activities, CTA. The order is the
         order an author works in — how the screen is arranged, what it says, what the kid does,
         and only then how they leave it. -->
    <template v-else>
      <AppText name="body1-medium">
        Screen
      </AppText>

      <section class="screen-editor__section">
        <!-- An absent `layout` is DISPLAYED as `single` and written back only when the author
             picks a value — defaulting it on load would be a document transform (decision B9). -->
        <NativeSelect
          name="layout"
          label="Layout"
          :options="refLayoutOptions"
          :value="refLayout"
          @change="(option: NativeSelectOption) => setLayout(option.value as ScreenLayout)"
        />
      </section>

      <section
        v-if="refOwner"
        class="screen-editor__section"
      >
        <ContentBlockList
          :owner="refOwner"
          label="Screen content"
        />
      </section>

      <!-- Below the screen's own content, matching the two-column player layout: shared content
           first, then the activities about it. -->
      <section class="screen-editor__section">
        <ActivityList :screen-id="refScreen.id" />
      </section>

      <!-- Last, because the CTA is what the kid presses when everything above it is done — and
           because "this screen's CTA submits the attempt" only means something once the author
           has seen the questions it submits. -->
      <section class="screen-editor__section">
        <AppText name="body2-medium">
          CTA
        </AppText>

        <InputPrimary
          name="cta"
          label="CTA label"
          optional-label="optional"
          :value="refCta"
          placeholder="Overrides the forward button's label"
          @input="(event: InputPrimaryEvent) => setCta(String(event.value))"
        />

        <BtnCheckbox
          text="This screen's CTA submits the attempt"
          :checked="refIsSubmitScreen"
          @click="toggleSubmitScreen"
        />
      </section>
    </template>
  </div>
</template>

<style scoped lang="scss" src="./ScreenEditor.scss" />
